import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import GemCard from '../components/GemCard.jsx';
import LinkCard from '../components/LinkCard.jsx';
import NotebookSampleCard from '../components/NotebookSampleCard.jsx';
import PromptCard from '../components/PromptCard.jsx';
import ShortcutsTab from '../components/ShortcutsTab.jsx';
import { tagColor } from '../utils/tagColor.js';

const TAG_PILL_SELECTED = {
  blue: 'bg-indigo-600 border-indigo-600 text-white',
  teal: 'bg-teal-600 border-teal-600 text-white',
  green: 'bg-emerald-600 border-emerald-600 text-white',
  yellow: 'bg-amber-500 border-amber-500 text-white',
  red: 'bg-red-600 border-red-600 text-white',
  pink: 'bg-pink-600 border-pink-600 text-white',
  purple: 'bg-violet-600 border-violet-600 text-white',
  orange: 'bg-orange-500 border-orange-500 text-white',
  cyan: 'bg-sky-500 border-sky-500 text-white',
  lime: 'bg-lime-600 border-lime-600 text-white',
  rose: 'bg-rose-600 border-rose-600 text-white',
  fuchsia: 'bg-fuchsia-600 border-fuchsia-600 text-white',
};

const TAG_PILL_IDLE = {
  blue: 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100',
  teal: 'bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100',
  yellow: 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100',
  red: 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100',
  pink: 'bg-pink-50 border-pink-200 text-pink-700 hover:bg-pink-100',
  purple: 'bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100',
  orange: 'bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100',
  cyan: 'bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100',
  lime: 'bg-lime-50 border-lime-200 text-lime-700 hover:bg-lime-100',
  rose: 'bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100',
  fuchsia: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-100',
};

const PDFViewer = lazy(() => import('../components/PDFViewer.jsx'));

const TabIcon = ({ id, className = 'w-4 h-4' }) => {
  const icons = {
    agenda: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
    materials: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    gems: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    notebooks: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
    prompts: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    shortcuts: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    form: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
  };
  return icons[id] || null;
};

const BASE_TABS = [
  { id: 'agenda',             label: 'アジェンダ' },
  { id: 'gemini-spreadsheet', label: 'Gemini in スプレッドシート' },
  { id: 'gemini-docs',        label: 'Gemini in ドキュメント' },
  { id: 'gemini-gmail',       label: 'Gemini in Gmail' },
  { id: 'gems',               label: 'Gems' },
  { id: 'notebooks',          label: 'NotebookLM' },
  { id: 'prompts',            label: 'プロンプト' },
  { id: 'shortcuts',          label: 'ショートカット' },
];

const TABS = [...BASE_TABS, { id: 'form', label: 'アンケート' }];

function toEmbedUrl(url) {
  if (!url) return '';
  const base = url.split('?')[0].replace(/\/(edit|prefill)$/, '/viewform');
  return `${base}?embedded=true`;
}

function isPdfUrl(url) {
  return url.startsWith('/api/files/') || url.toLowerCase().includes('.pdf');
}

// ── Inline Viewer ─────────────────────────────────────────────────────────────
function InlineViewer({ url, name, onBack, token }) {
  const isPdf = isPdfUrl(url);
  const isR2 = url.startsWith('/api/files/');
  const [blobUrl, setBlobUrl] = useState(null);
  const [fetchError, setFetchError] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  useEffect(() => {
    if (!isR2) return;
    setBlobUrl(null);
    setFetchError(false);
    let objectUrl;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => { objectUrl = URL.createObjectURL(blob); setBlobUrl(objectUrl); })
      .catch(() => setFetchError(true));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [url, token, isR2]);

  const dlHref = isR2 ? `${url}?dl=1&token=${token}` : url;
  const effectiveUrl = isR2 ? blobUrl : url;

  const copyUrl = () => {
    navigator.clipboard.writeText(url).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const Spinner = () => (
    <div className="flex items-center justify-center h-64 gap-3 text-slate-500">
      <div className="w-5 h-5 border-2 border-slate-200 border-t-indigo-500 rounded-full animate-spin" />
      <span className="text-sm">読み込み中...</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        {onBack && (
          <button onClick={onBack}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors shadow-card">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            一覧
          </button>
        )}
        {name && <span className="text-sm font-medium text-slate-700 truncate max-w-xs">{name}</span>}
        {!isPdf && (
          <button onClick={copyUrl}
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors shadow-card cursor-pointer"
            title="URLをコピー">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            {urlCopied ? 'コピー済み' : 'URLコピー'}
          </button>
        )}
        {isPdf && (
          <a href={dlHref} download
            className="flex items-center gap-1.5 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 transition-colors shadow-card">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            ダウンロード
          </a>
        )}
      </div>

      {fetchError ? (
        <div className="flex items-center justify-center h-64 text-red-500 text-sm">
          ファイルの読み込みに失敗しました
        </div>
      ) : isPdf ? (
        isR2 && !blobUrl ? <Spinner /> : (
          <Suspense fallback={<Spinner />}>
            <PDFViewer url={effectiveUrl} />
          </Suspense>
        )
      ) : (
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-card"
          style={{ height: 'calc(100vh - 220px)', minHeight: '400px' }}>
          <iframe src={url} title={name || 'ドキュメント'} className="w-full h-full block" />
        </div>
      )}

      {!isPdf && (
        <p className="text-xs text-slate-400 text-center">
          表示されない場合はURLを直接開いてご確認ください
        </p>
      )}
    </div>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────────
function ParticipantLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const tenantId = new URLSearchParams(window.location.search).get('tenant') || null;
      const res = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, role: 'participant', tenant_id: tenantId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'ログインに失敗しました');
      onLogin(data.token);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)' }}>
      <div className="absolute -top-1/3 -left-1/4 w-[600px] h-[600px] bg-indigo-700/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-1/4 -right-1/4 w-[500px] h-[500px] bg-violet-700/15 rounded-full blur-3xl pointer-events-none" />
      <div className="relative w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <div className="w-14 h-14 bg-indigo-500 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-500/40">
            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        </div>
        <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          <h1 className="text-2xl font-bold text-white text-center mb-1.5 font-display">研修リソースポータル</h1>
          <p className="text-sm text-slate-400 text-center mb-8">パスワードを入力してください</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.08] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/50 transition-all"
              placeholder="パスワード" autoFocus />
            {error && (
              <p className="text-sm text-red-300 bg-red-500/10 border border-red-400/20 px-3 py-2.5 rounded-xl">{error}</p>
            )}
            <button type="submit" disabled={loading}
              className="py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-medium disabled:opacity-50 transition-all text-sm shadow-lg shadow-indigo-600/30 active:scale-[0.98]">
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Portal Main ───────────────────────────────────────────────────────────────
export default function Portal() {
  const [isPreview] = useState(() => localStorage.getItem('portal_preview_token') ?? null);
  const [token, setToken] = useState(() => isPreview || localStorage.getItem('participant_token'));
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('agenda');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedSample, setSelectedSample] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const dataUrl = isPreview ? '/api/data?as=participant' : '/api/data';

  // isPreview トークンの削除は副作用なので useEffect で行う
  useEffect(() => {
    if (isPreview) localStorage.removeItem('portal_preview_token');
  }, []);

  const handleLogin = (t) => {
    localStorage.setItem('participant_token', t);
    setToken(t);
    setLoading(true);
    setError(null);
  };

  const handleLogout = () => {
    localStorage.removeItem('participant_token');
    setToken(null);
    setData(null);
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    fetch(dataUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (r.status === 401) {
          localStorage.removeItem('participant_token');
          setToken(null);
          throw new Error('セッションが切れました');
        }
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const id = setInterval(() => {
      fetch(dataUrl, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => {
          if (r.status === 401) { localStorage.removeItem('participant_token'); setToken(null); return null; }
          return r.json();
        })
        .then(d => { if (d) setData(d); })
        .catch(() => {});
    }, 30000);
    return () => clearInterval(id);
  }, [token, dataUrl]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSelectedTag('all');
    setSelectedMaterial(null);
  };

  const getItemsAndTags = () => {
    if (!data) return { items: [], tags: [] };

    let list = [];
    let tagKey = null;

    // アクティブなタブに基づいて リスト を取得
    switch(activeTab) {
      case 'gems':
        list = data.gems || [];
        tagKey = 'tag';
        break;
      case 'notebooks':
        list = data.notebooks || [];
        tagKey = 'tag';
        break;
      case 'prompts':
        list = data.prompts || [];
        tagKey = 'tag';
        break;
      case 'gemini-spreadsheet':
        list = (data.materials || []).filter(m => m.name.includes('スプレッドシート'));
        break;
      case 'gemini-docs':
        list = (data.materials || []).filter(m => m.name.includes('ドキュメント'));
        break;
      case 'gemini-gmail':
        list = (data.materials || []).filter(m => m.name.includes('Gmail'));
        break;
      case 'agenda':
      default:
        list = [];
    }

    const unique = tagKey ? [...new Set((list || []).map(i => i[tagKey]).filter(Boolean))] : [];
    return { items: list || [], tags: unique };
  };

  const { items, tags } = getItemsAndTags();

  const filtered = selectedTag === 'all' ? items : items.filter(i => (i.tag || '') === selectedTag);

  if (!token) return <ParticipantLogin onLogin={handleLogin} />;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          <span className="text-sm text-slate-500">読み込み中...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page p-4">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="font-semibold text-slate-800 mb-1">データの取得に失敗しました</p>
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const title      = data?.settings?.training_title || '研修リソースポータル';
  const agendaUrl  = data?.settings?.agenda_url || '';
  const formUrl    = data?.settings?.form_url || '';
  const formQrUrl  = data?.settings?.form_qr_url || '';
  const trainingEdition = data?.settings?.training_edition || 'google_workspace';

  // 管理者が設定した training_edition に対応したセクション（edition が null=共通 または 該当版）
  const sections = (data?.sections || []).filter(s => !s.edition || s.edition === trainingEdition);

  return (
    <div className="min-h-screen bg-page flex flex-col">
      {isPreview && (
        <div className="print:hidden bg-amber-400/20 border-b border-amber-200 px-4 py-2 flex items-center justify-between">
          <span className="text-xs font-medium text-amber-800">
            プレビューモード — 参加者から見える画面を表示しています（非表示アイテムは除外）
          </span>
          <button onClick={() => window.close()} className="text-xs font-medium text-amber-700 underline hover:no-underline">
            タブを閉じる
          </button>
        </div>
      )}

      <header className="print:hidden bg-slate-900 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3.5 flex items-center justify-between">
          <h1 className="text-sm font-semibold text-white leading-tight font-display tracking-wide">{title}</h1>
          {!isPreview && (
            <button onClick={handleLogout}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors px-2.5 py-1.5 rounded-lg hover:bg-white/5">
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              ログアウト
            </button>
          )}
        </div>
        <div className="max-w-5xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-0 min-w-max border-t border-white/5">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap cursor-pointer ${activeTab === tab.id ? 'border-indigo-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}>
                <TabIcon id={tab.id} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6 flex flex-col gap-4">

        {/* アジェンダ */}
        {activeTab === 'agenda' && (
          agendaUrl
            ? <InlineViewer url={agendaUrl} name="アジェンダ" token={token} />
            : <EmptyState id="agenda" message="アジェンダはまだ設定されていません" />
        )}

        {/* Gemini in スプレッドシート */}
        {activeTab === 'gemini-spreadsheet' && (
          selectedMaterial ? (
            selectedMaterial.content ? (
              <div className="flex flex-col gap-4">
                <button onClick={() => setSelectedMaterial(null)}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  戻る
                </button>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">{selectedMaterial.name}</h2>
                  <div className="prose prose-sm max-w-none">
                    <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 overflow-x-auto text-xs text-slate-700 font-mono whitespace-pre-wrap break-words">
                      {selectedMaterial.content}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <InlineViewer
                url={selectedMaterial.url}
                name={selectedMaterial.name}
                onBack={() => setSelectedMaterial(null)}
                token={token}
              />
            )
          ) : (
            <div className="flex flex-col gap-4">
              {filtered.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(m => <LinkCard key={m.id} item={m} onView={() => setSelectedMaterial(m)} />)}
                </div>
              ) : (
                <EmptyState id="gemini-spreadsheet" message="資料がまだ登録されていません" />
              )}
            </div>
          )
        )}

        {/* Gemini in ドキュメント */}
        {activeTab === 'gemini-docs' && (
          selectedMaterial ? (
            selectedMaterial.content ? (
              <div className="flex flex-col gap-4">
                <button onClick={() => setSelectedMaterial(null)}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  戻る
                </button>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">{selectedMaterial.name}</h2>
                  <div className="prose prose-sm max-w-none">
                    <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 overflow-x-auto text-xs text-slate-700 font-mono whitespace-pre-wrap break-words">
                      {selectedMaterial.content}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <InlineViewer
                url={selectedMaterial.url}
                name={selectedMaterial.name}
                onBack={() => setSelectedMaterial(null)}
                token={token}
              />
            )
          ) : (
            <div className="flex flex-col gap-4">
              {filtered.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(m => <LinkCard key={m.id} item={m} onView={() => setSelectedMaterial(m)} />)}
                </div>
              ) : (
                <EmptyState id="gemini-docs" message="資料がまだ登録されていません" />
              )}
            </div>
          )
        )}

        {/* Gemini in Gmail */}
        {activeTab === 'gemini-gmail' && (
          selectedMaterial ? (
            selectedMaterial.content ? (
              <div className="flex flex-col gap-4">
                <button onClick={() => setSelectedMaterial(null)}
                  className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  戻る
                </button>
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <h2 className="text-lg font-bold text-slate-800 mb-4">{selectedMaterial.name}</h2>
                  <div className="prose prose-sm max-w-none">
                    <pre className="bg-slate-50 border border-slate-200 rounded-xl p-4 overflow-x-auto text-xs text-slate-700 font-mono whitespace-pre-wrap break-words">
                      {selectedMaterial.content}
                    </pre>
                  </div>
                </div>
              </div>
            ) : (
              <InlineViewer
                url={selectedMaterial.url}
                name={selectedMaterial.name}
                onBack={() => setSelectedMaterial(null)}
                token={token}
              />
            )
          ) : (
            <div className="flex flex-col gap-4">
              {filtered.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(m => <LinkCard key={m.id} item={m} onView={() => setSelectedMaterial(m)} />)}
                </div>
              ) : (
                <EmptyState id="gemini-gmail" message="資料がまだ登録されていません" />
              )}
            </div>
          )
        )}

        {/* Gems */}
        {activeTab === 'gems' && (() => {
          const sharedGems = (data?.gems || []).filter(g => !g.gem_type || g.gem_type === 'shared');
          const sampleGems = (data?.gems || []).filter(g => g.gem_type === 'sample');
          const filteredShared = selectedTag === 'all' ? sharedGems : sharedGems.filter(g => g.tag === selectedTag);
          const filteredSample = selectedTag === 'all' ? sampleGems : sampleGems.filter(g => g.tag === selectedTag);
          const hasAny = sharedGems.length > 0 || sampleGems.length > 0;
          return (
            <>
              <TagFilter tags={tags} items={items} selectedTag={selectedTag} onSelect={setSelectedTag} />
              {!hasAny ? (
                <EmptyState id="gems" message="Gemがまだ登録されていません" />
              ) : (
                <>
                  {sharedGems.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeading>共有 Gem</SectionHeading>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredShared.length > 0
                          ? filteredShared.map(g => <GemCard key={g.id} gem={g} />)
                          : <p className="text-sm text-slate-400 col-span-full">このカテゴリーには共有Gemがありません</p>}
                      </div>
                    </div>
                  )}
                  {sampleGems.length > 0 && (
                    <div className="flex flex-col gap-3">
                      <SectionHeading>サンプルデータ</SectionHeading>
                      <p className="text-xs text-slate-400 -mt-1">システムプロンプトをコピーして自分だけのGemを作ってみましょう</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredSample.length > 0
                          ? filteredSample.map(g => <GemCard key={g.id} gem={g} />)
                          : <p className="text-sm text-slate-400 col-span-full">このカテゴリーにはサンプルデータがありません</p>}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          );
        })()}

        {/* NotebookLM */}
        {activeTab === 'notebooks' && (
          selectedSample ? (
            <div className="flex flex-col gap-4">
              <button onClick={() => setSelectedSample(null)}
                className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 transition-colors cursor-pointer">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                一覧に戻る
              </button>
              <div className="bg-white border border-slate-200 rounded-2xl p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4">{selectedSample.name}</h2>
                {selectedSample.desc && <p className="text-sm text-slate-600 mb-4">{selectedSample.desc}</p>}
                {Array.isArray(selectedSample.sources) && selectedSample.sources.length > 0 && (
                  <div className="flex flex-col gap-3">
                    {selectedSample.sources.map((src, i) => (
                      <div key={i} className="flex flex-col gap-2 border border-slate-100 rounded-xl p-4 bg-slate-50">
                        <p className="text-sm font-semibold text-slate-700">{src.label}</p>
                        {src.type === 'text' && src.content && (
                          <pre className="text-sm text-slate-600 bg-white border border-slate-200 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">{src.content}</pre>
                        )}
                        {src.type !== 'text' && src.url && (
                          <div className="flex items-center gap-2">
                            <code className="flex-1 text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 text-slate-700 truncate font-mono">{src.url}</code>
                            <button onClick={() => navigator.clipboard.writeText(src.url)}
                              className="px-3 py-2 text-xs bg-slate-200 hover:bg-slate-300 text-slate-600 rounded-lg transition-colors cursor-pointer">
                              コピー
                            </button>
                            <a href={src.url} target="_blank" rel="noopener noreferrer"
                              className="px-3 py-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors cursor-pointer flex items-center gap-1">
                              開く
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (() => {
            const sharedNbs = (data?.notebooks || []).filter(n => !n.notebook_type || n.notebook_type === 'shared');
            const sampleNbs = (data?.notebooks || []).filter(n => n.notebook_type === 'sample');
            const filteredShared = selectedTag === 'all' ? sharedNbs : sharedNbs.filter(n => n.tag === selectedTag);
            const filteredSample = selectedTag === 'all' ? sampleNbs : sampleNbs.filter(n => n.tag === selectedTag);
            const hasAny = sharedNbs.length > 0 || sampleNbs.length > 0;
            return (
              <>
                <TagFilter tags={tags} items={items} selectedTag={selectedTag} onSelect={setSelectedTag} />
                {!hasAny ? (
                  <EmptyState id="notebooks" message="NotebookLMがまだ登録されていません" />
                ) : (
                  <>
                    {sharedNbs.length > 0 && (
                      <div className="flex flex-col gap-3">
                        <SectionHeading>共有ノート</SectionHeading>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {filteredShared.length > 0
                            ? filteredShared.map(n => <LinkCard key={n.id} item={n} />)
                            : <p className="text-sm text-slate-400 col-span-full">このカテゴリーには共有ノートがありません</p>}
                        </div>
                      </div>
                    )}
                    {sampleNbs.length > 0 && (
                      <div className="flex flex-col gap-3">
                        <SectionHeading>サンプルデータ</SectionHeading>
                        <p className="text-xs text-slate-400 -mt-1">新規ノートを作成して以下のソースを追加してみましょう</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {filteredSample.length > 0
                            ? filteredSample.map(n => <NotebookSampleCard key={n.id} item={n} onSelect={() => setSelectedSample({...n, sources: typeof n.sources === 'string' ? JSON.parse(n.sources || '[]') : (n.sources || [])})} />)
                            : <p className="text-sm text-slate-400 col-span-full">このカテゴリーにはサンプルデータがありません</p>}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()
        )}

        {/* プロンプト */}
        {activeTab === 'prompts' && (
          <>
            <TagFilter tags={tags} items={items} selectedTag={selectedTag} onSelect={setSelectedTag} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.length > 0
                ? filtered.map(p => <PromptCard key={p.id} prompt={p} />)
                : <EmptyState id="prompts" message={selectedTag !== 'all' ? 'このカテゴリーにはプロンプトがありません' : 'プロンプトがまだ登録されていません'} />}
            </div>
          </>
        )}

        {/* ショートカット */}
        {activeTab === 'shortcuts' && <ShortcutsTab />}

        {/* アンケート */}
        {activeTab === 'form' && (() => {
          const hasContent = formUrl || formQrUrl;
          const qrSrc = formQrUrl.startsWith('/api/files/') ? `${formQrUrl}?token=${token}` : formQrUrl;
          return hasContent ? (
            <div className="flex flex-col gap-6">
              {formQrUrl && (
                <div className="flex flex-col items-center gap-3 py-6">
                  <img src={qrSrc} alt="アンケートQRコード"
                    className="w-56 h-56 object-contain rounded-2xl border border-slate-200 shadow-md bg-white p-3" />
                  <p className="text-sm text-slate-500">スマートフォンでQRコードを読み取って回答できます</p>
                  {formUrl && (
                    <a href={formUrl} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
                      フォームを別タブで開く
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  )}
                </div>
              )}
              {formUrl && (
                <div className="flex flex-col gap-3">
                  <div className="rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-card"
                    style={{ height: 'calc(100vh - 220px)', minHeight: '500px' }}>
                    <iframe src={toEmbedUrl(formUrl)} title="アンケート" className="w-full h-full block"
                      frameBorder="0" marginHeight="0" marginWidth="0">読み込み中...</iframe>
                  </div>
                  {!formQrUrl && (
                    <p className="text-xs text-slate-400 text-center">
                      表示されない場合は{' '}
                      <a href={formUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600">
                        こちらから別タブで開く
                      </a>
                    </p>
                  )}
                </div>
              )}
            </div>
          ) : (
            <EmptyState id="form" message="アンケートフォームが設定されていません" />
          );
        })()}
      </main>
    </div>
  );
}

// ── 研修資料タブ — セクション別グループ表示 ───────────────────────────────────
function MaterialsTab({ materials, sections, onSelect }) {
  if (materials.length === 0) {
    return <EmptyState id="materials" message="資料がまだ登録されていません" />;
  }

  // セクションに紐づく資料
  const bySectionId = {};
  for (const m of materials) {
    const key = m.section_id || '__none__';
    if (!bySectionId[key]) bySectionId[key] = [];
    bySectionId[key].push(m);
  }

  // sections の順番通りに表示（未分類は最後）
  const uncategorized = bySectionId['__none__'] || [];

  return (
    <div className="flex flex-col gap-8">
      {sections.map(sec => {
        const items = bySectionId[sec.id] || [];
        if (items.length === 0) return null;
        return (
          <div key={sec.id} className="flex flex-col gap-3">
            <SectionHeading>{sec.icon} {sec.name}</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(m => (
                <LinkCard
                  key={m.id}
                  item={m}
                  onView={(m.url || m.content) ? () => onSelect(m) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}
      {uncategorized.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionHeading>📁 その他</SectionHeading>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {uncategorized.map(m => (
              <LinkCard
                key={m.id}
                item={m}
                onView={(m.url || m.content) ? () => onSelect(m) : undefined}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-widest border-l-2 border-indigo-400 pl-2.5">{children}</h2>
  );
}

function TagFilter({ tags, items, selectedTag, onSelect }) {
  if (tags.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button onClick={() => onSelect('all')}
        className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${selectedTag === 'all' ? 'bg-slate-800 border-slate-800 text-white shadow-sm' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:text-slate-800'}`}>
        すべて ({items.length})
      </button>
      {tags.map(tag => {
        const color = tagColor(tag) || 'blue';
        const isSelected = selectedTag === tag;
        return (
          <button key={tag} onClick={() => onSelect(tag)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors cursor-pointer ${isSelected ? TAG_PILL_SELECTED[color] : TAG_PILL_IDLE[color]}`}>
            {tag} ({items.filter(i => i.tag === tag).length})
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ id, message }) {
  return (
    <div className="col-span-full flex flex-col items-center justify-center py-20 text-slate-400">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mb-4">
        <TabIcon id={id} className="w-7 h-7 text-slate-400" />
      </div>
      <p className="text-sm font-medium">{message}</p>
    </div>
  );
}
